/* eslint-disable @typescript-eslint/no-explicit-any -- converting existing file*/
import { usePostHog } from "@posthog/react";
import type { AuthError, Session, User } from "@supabase/supabase-js";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AuthMachineStatus } from "../constants";
import { createAPI, createPublicAPI } from "../services/apiService";
import type { ResetPasswordResponse } from "../services/authService";
import { AuthService } from "../services/authService";
import supabase from "../services/supabaseClient";
import {
  authOperations,
  getAuthSession,
  initAuthMachine,
  setAuthSession,
  useAuthMachineState,
} from "../store/authMachine";
import { usePermissionsV2Store } from "../store/permissionsV2Store";
import { usePreferencesStore } from "../store/preferencesStore";

// Types
export interface FirmSettings {
  [key: string]: any;
}
interface Permissions {
  super_admin?: boolean;
  case_access?: {
    all_within_firm?: boolean;
    own?: boolean;
    assigned?: boolean;
  };
  [key: string]: any;
}
export interface FirmContext {
  firmId: string;
  roleName: string;
  permissions: Permissions;
  firmName: string;
  firmSettings: FirmSettings;
  lastUpdated: number;
}
export interface UserFirm {
  firmId: string;
  roleName: string;
  firmName: string;
}
interface AuthTokens {
  access_token: string;
  refresh_token: string;
}
export interface SignInCredentials {
  email: string;
  password: string;
}

type UserLoginResponse =
  | { user: User; error: null }
  | { user: null; needsEmailConfirmation: true; error: null }
  | { user: null; error: Error };

export interface AuthContextValue {
  user: User | null;
  refreshSession: (forceRefresh?: boolean) => Promise<Session | null>;
  getAccessToken: () => Promise<AuthTokens | null>;
  resetPasswordForEmail: (email: string) => Promise<ResetPasswordResponse>;
  signIn: (credentials: SignInCredentials) => Promise<UserLoginResponse>;
  signOut: () => Promise<{ error: AuthError | null }>;
  updatePassword: (password: string) => Promise<{ user: User }>;
  firmContext: FirmContext | null;
  hasPermission: (category: string, action?: string) => boolean;
  canAccessCase: (caseId: string) => Promise<boolean>;
  getFirmRoleData: (firmId: string, forceRefresh?: boolean) => Promise<FirmContext | null>;
  clearFirmRoleCache: () => void;
  userFirms: UserFirm[];
  switchFirm: (firmId: string) => Promise<void>;
  refreshFirmContext: (force_refresh?: boolean) => Promise<void>;
  isFreshSignIn: boolean;
  markSignInComplete: () => void;
  isMyCases: boolean;
  setIsMyCases: (val: boolean) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const machineState = useAuthMachineState();
  const user = machineState.user;
  const loading = machineState.status === AuthMachineStatus.BOOTING;
  const isFreshSignIn = machineState.isFreshSignIn;

  const [firmContext, setFirmContext] = useState<FirmContext | null>(null);
  // Ref, not state: cache writes are an internal implementation detail of
  // getFirmRoleData's memoization and must not trigger a context re-render or
  // appear in getFirmRoleData's dependency array (a state-backed cache would
  // change identity on every write, invalidating every value that depends on it).
  const firmRoleCacheRef = useRef<Map<string, FirmContext>>(new Map());
  const [userFirms, setUserFirms] = useState<UserFirm[]>([]);
  const [isMyCases, setIsMyCases] = useState<boolean>(false);

  // The module-singleton useAuthMachineStore persists across AuthProvider (re)mounts
  // within the same JS lifetime and is only trustworthy for THIS mount once
  // initAuthMachine() has reset it — until then it can still hold stale state from a
  // previous mount's teardown (e.g. status "expired" from a prior sign-out). Since the
  // reset happens in an effect, which runs after the first render commits, that first
  // render would otherwise paint against stale state. Gate on machineReady so nothing
  // renders before the reset — matching the old behavior where `loading` started
  // `true` via useState and the pre-effect render was blank.
  const [machineReady, setMachineReady] = useState(false);

  // Boots the auth state machine (session/user/status transitions, single source of
  // truth) once per provider mount. StrictMode double-invokes effects in dev/test —
  // initAuthMachine resets the store and rewires subscriptions each call, and its
  // cleanup unsubscribes, so the double-mount leaves exactly one live subscription.
  // machineReady is component state, so each mount instance gates only its own render.
  useEffect(() => {
    const cleanup = initAuthMachine();
    setMachineReady(true);
    return cleanup;
  }, []);

  const resetFirmState = useCallback(() => {
    setFirmContext(null);
    firmRoleCacheRef.current = new Map();
    setUserFirms([]);
    setIsMyCases(false);
  }, []);

  // Firm-local state is scoped to the signed-in user; clear it once the machine
  // leaves the authenticated family so a subsequent sign-in starts clean.
  useEffect(() => {
    if (
      machineState.status === AuthMachineStatus.UNAUTHENTICATED ||
      machineState.status === AuthMachineStatus.EXPIRED
    ) {
      resetFirmState();
    }
  }, [machineState.status, resetFirmState]);

  const getFirmRoleData = useCallback(
    async (firmId: string, forceRefresh: boolean = false): Promise<FirmContext | null> => {
      if (!user) return null;

      const cacheKey = `${firmId}-${user.id}`;
      // Snapshot is only for the cache-hit check below — the write after the
      // await must read the ref again at that time (see below), since another
      // overlapping call may have written a different key into it while this
      // fetch was in flight.
      const firmRoleCacheAtCallTime = firmRoleCacheRef.current;

      if (!forceRefresh && firmRoleCacheAtCallTime.has(cacheKey)) {
        return firmRoleCacheAtCallTime.get(cacheKey) || null;
      }

      try {
        const { data, error } = await supabase
          .from("firm_users")
          .select(
            `
          firm_id,
          roles (
            role_name,
            permissions
          ),
          firms (
            name,
            settings
          )
        `
          )
          .eq("firm_id", firmId)
          .eq("user_id", user.id)
          .single();

        if (error) throw error;

        const result = data as any;

        const settings: FirmSettings = {
          ...result.firms.settings,
        };

        const firmRoleData: FirmContext = {
          firmId: result.firm_id,
          roleName: result.roles.role_name,
          permissions: result.roles.permissions,
          firmName: result.firms.name,
          firmSettings: settings,
          lastUpdated: new Date().getTime(),
        };

        // Read the ref again here (not the pre-await snapshot above) — an
        // overlapping getFirmRoleData call may have written a different key
        // into it while this fetch was in flight; building off the stale
        // snapshot would silently drop that write.
        firmRoleCacheRef.current = new Map(firmRoleCacheRef.current).set(cacheKey, firmRoleData);

        return firmRoleData;
      } catch (error) {
        console.error("Error fetching firm role data:", error);
        return null;
      }
    },
    [user?.id]
  );

  const fetchFirmContext = useCallback(
    async (force_refresh: boolean = false) => {
      try {
        if (!user?.id) {
          setFirmContext(null);
          return;
        }
        const { data, error } = await supabase
          .from("firm_users")
          .select("firm_id")
          .eq("user_id", user.id)
          .single();

        if (error) {
          console.error("[AuthContext] Error fetching firm context:", error);
          return;
        }

        try {
          const firmRoleData = await getFirmRoleData(data.firm_id, force_refresh);
          if (firmRoleData) {
            if (firmRoleData.roleName === "super_analyst") {
              const {
                selectedPseudoFirm,
                firmSelectionMade,
                setSelectedPseudoFirm,
                setFirmSelectionMade,
              } = usePreferencesStore.getState();

              if (selectedPseudoFirm === "all_assigned_cases" || !firmSelectionMade) {
                setSelectedPseudoFirm("all_assigned_cases");
                setFirmSelectionMade(true);
                setIsMyCases(true);
              } else {
                setIsMyCases(false);
              }
            } else {
              setIsMyCases(false);
            }

            setFirmContext(firmRoleData);
          } else {
            console.error("[AuthContext] No firm role data returned");
          }
        } catch (error) {
          console.error("[AuthContext] Error getting firm role data:", error);
        }
      } catch (error) {
        console.error("[AuthContext] Unexpected error in firm context fetch:", error);
      }
    },
    [user?.id, getFirmRoleData]
  );

  // Fetch firm context whenever user changes
  useEffect(() => {
    if (!user?.id) return;
    fetchFirmContext().catch((error) => {
      console.error("[AuthContext] Unhandled error in firm context fetch:", error);
    });
  }, [user?.id]);

  // Helper to check permissions
  const hasPermission = useCallback(
    (category: string, action?: string): boolean => {
      if (!firmContext?.permissions) return false;

      if (firmContext.permissions.super_admin) return true;

      if (action === undefined) {
        return !!firmContext.permissions[category];
      }

      return !!firmContext.permissions[category]?.[action];
    },
    [firmContext]
  );

  // Helper to check case access
  const canAccessCase = useCallback(
    async (caseId: string): Promise<boolean> => {
      if (!user?.id || !firmContext) return false;

      if (firmContext.permissions.super_admin) return true;

      const { data: caseData } = await supabase
        .from("cases")
        .select("firm_id, user_id")
        .eq("case_id", caseId)
        .single();

      if (!caseData) return false;

      const caseAccess = firmContext.permissions?.case_access || {};

      if (caseAccess.all_within_firm && caseData.firm_id === firmContext.firmId) {
        return true;
      }

      if (caseAccess.own && caseData.user_id === user.id) {
        return true;
      }

      if (caseAccess.assigned) {
        const { data: analyst } = await supabase
          .from("case_analysts")
          .select("user_id")
          .eq("case_id", caseId)
          .eq("user_id", user.id)
          .single();

        if (analyst) return true;
      }

      return false;
    },
    [user?.id, firmContext]
  );

  /**
   * Returns the current valid session, refreshing it if necessary.
   *
   * Used by getAccessToken (Axios interceptor) to supply a fresh bearer token.
   * State management (auth status, user cleanup) is handled by onAuthStateChange —
   * a failed refresh fires SIGNED_OUT which resets all auth state automatically.
   */
  const refreshSession = useCallback(
    async (forceRefresh: boolean = false): Promise<Session | null> => {
      try {
        const session = getAuthSession();
        const now = Math.floor(Date.now() / 1000);
        if (
          !forceRefresh &&
          session?.access_token &&
          session.expires_at &&
          session.expires_at > now
        ) {
          return session;
        }

        const {
          data: { session: newSession },
          error,
        } = await supabase.auth.refreshSession();

        if (error) {
          console.error("[AuthContext] Session refresh error:", error);
          // SIGNED_OUT event will fire and handle state cleanup via onAuthStateChange
          return null;
        }

        setAuthSession(newSession);
        return newSession;
      } catch (error) {
        console.error("[AuthContext] Unexpected error refreshing session:", error);
        return null;
      }
    },
    []
  );

  const getAccessToken = useCallback(async (): Promise<AuthTokens | null> => {
    const currentSession = await refreshSession();
    return currentSession
      ? {
          access_token: currentSession.access_token,
          refresh_token: currentSession.refresh_token,
        }
      : null;
  }, [refreshSession]);

  /**
   * True while the machine is authenticated or passwordResetPending.
   * - A boolean, not the raw status, so authenticated ↔ passwordResetPending does not reload permissions.
   */
  const hasLiveSession =
    machineState.status === AuthMachineStatus.AUTHENTICATED ||
    machineState.status === AuthMachineStatus.PASSWORD_RESET_PENDING;

  const loadPermissionsV2 = useCallback(async () => {
    if (!user?.id || !hasLiveSession) {
      usePermissionsV2Store.getState().clearPermissions();
      return;
    }

    try {
      const v2Api = createAPI(getAccessToken, "v2");
      await usePermissionsV2Store.getState().loadFromApi(v2Api);
    } catch (error) {
      console.error("[AuthContext] Error loading v2 permissions:", error);
      usePermissionsV2Store.getState().clearPermissions();
    }
  }, [user?.id, hasLiveSession, getAccessToken]);

  useEffect(() => {
    loadPermissionsV2().catch((error) => {
      console.error("[AuthContext] Unhandled error loading v2 permissions:", error);
    });
  }, [loadPermissionsV2]);

  const posthog = usePostHog();

  const userId = user?.id;
  const userEmail = user?.email;
  useEffect(() => {
    if (!posthog) return;
    if (userId) {
      posthog.identify(userId, { email: userEmail });
      if (firmContext?.roleName) {
        posthog.register({ role: firmContext.roleName });
      }
    } else {
      posthog.reset();
    }
  }, [posthog, userId, userEmail, firmContext?.roleName]);

  useEffect(() => {
    const fetchUserFirms = async () => {
      try {
        if (!user) {
          setUserFirms([]);
          return;
        }

        const { data, error } = await supabase
          .from("firm_users")
          .select(
            `
            firm_id,
            roles (role_name),
            firms (name)
          `
          )
          .eq("user_id", user.id);

        if (error) {
          console.error("[AuthContext] Error fetching user firms:", error);
          return;
        }

        const firms = data.map((fu: any) => ({
          firmId: fu.firm_id,
          roleName: fu.roles.role_name,
          firmName: fu.firms.name,
        }));

        setUserFirms(firms);
      } catch (error) {
        console.error("[AuthContext] Unexpected error fetching firms:", error);
      }
    };

    void fetchUserFirms();
  }, [user?.id]);

  const switchFirm = useCallback(
    async (firmId: string): Promise<void> => {
      if (!user?.id) return;

      const firmRoleData = await getFirmRoleData(firmId, true);
      if (firmRoleData) {
        setFirmContext(firmRoleData);
        await loadPermissionsV2();
      }
    },
    [user?.id, getFirmRoleData, loadPermissionsV2]
  );

  const resetPasswordForEmail = useCallback(
    async (email: string): Promise<ResetPasswordResponse> => {
      const publicApi = createPublicAPI("v2");
      const authService = new AuthService(publicApi);
      return await authService.resetPasswordForEmail(email);
    },
    []
  );

  const signIn = useCallback(async (credentials: SignInCredentials): Promise<UserLoginResponse> => {
    try {
      const { email, password } = credentials;

      const { user, session, error } = await authOperations.signIn(email, password);

      if (error) {
        console.error("[AuthContext] Sign in error:", error);
        return { user: null, error };
      }

      if (user && session) {
        return { user, error: null };
      }

      console.error("[AuthContext] Sign in failed: No user or session");
      return { user: null, error: new Error("Sign in failed: No user or session returned") };
    } catch (error) {
      console.error("[AuthContext] Unexpected error during sign in:", error);
      return { user: null, error: error as Error };
    }
  }, []);

  const signOut = useCallback(async (): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await authOperations.signOut();
      if (error) {
        console.error("[AuthContext] Sign out error:", error);
      }
      return { error };
    } catch (err) {
      console.error("[AuthContext] Unexpected error during sign out:", err);
      throw err;
    }
  }, []);

  const updatePassword = useCallback(async (password: string): Promise<{ user: User }> => {
    return authOperations.updatePassword(password);
  }, []);

  const clearFirmRoleCache = useCallback(() => {
    firmRoleCacheRef.current = new Map();
  }, []);

  const refreshFirmContext = useCallback(
    (force_refresh: boolean = false) => fetchFirmContext(force_refresh),
    [fetchFirmContext]
  );

  const markSignInComplete = useCallback(() => authOperations.markSignInComplete(), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      refreshSession,
      getAccessToken,
      resetPasswordForEmail,
      signIn,
      signOut,
      updatePassword,
      firmContext,
      hasPermission,
      canAccessCase,
      getFirmRoleData,
      clearFirmRoleCache,
      userFirms,
      switchFirm,
      refreshFirmContext,
      isFreshSignIn,
      markSignInComplete,
      isMyCases,
      setIsMyCases,
    }),
    [
      user,
      refreshSession,
      getAccessToken,
      resetPasswordForEmail,
      signIn,
      signOut,
      updatePassword,
      firmContext,
      hasPermission,
      canAccessCase,
      getFirmRoleData,
      clearFirmRoleCache,
      userFirms,
      switchFirm,
      refreshFirmContext,
      isFreshSignIn,
      markSignInComplete,
      isMyCases,
      setIsMyCases,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {machineReady && !loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
